import {
  Alert,
  Paper,
  Skeleton,
  SortDirection,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material'
import { SxProps, Theme } from '@mui/material/styles'
import { TableContainerProps } from '@mui/material/TableContainer'
import React from 'react'

import useT from '@/i18n/useT'

import { FeatureUnavailable, TableNoData } from './DaiAlerts'

export type HeadCell<T = unknown> = {
  id: string
  alignment?: 'left' | 'center' | 'right' | 'justify' | 'inherit'
  label: string
  renderContent?: () => React.ReactNode
  padding?: 'normal' | 'checkbox' | 'none'
  sortKey?: keyof T
  tooltip?: string
}

export interface DeliciousTableProps<T, K> {
  data: T[]
  headCells: HeadCell<K>[]
  renderRow: (item: T, index: number) => React.ReactNode
  orderBy?: keyof K | null
  order?: SortDirection
  handleRequestSort?: (sortKey: keyof K) => void
  loading?: boolean
  error?: string
  totalCount?: number
  page?: number
  rowsPerPage?: number
  title?: React.ReactNode
  size?: 'small' | 'medium'
  onPageChange?: (newPage: number) => void
  onRowsPerPageChange?: (newRowsPerPage: number) => void
  featureAvailable?: boolean
  noDataType?: 'noData' | 'needToCollectData'
  noDataMessage?: string
  BodyComponent?: React.ReactNode
  RightHeaderComponent?: React.ReactNode
  stickyHeader?: boolean
  paperSx?: SxProps<Theme>
  tableContainerSx?: SxProps<Theme>
  tableSx?: SxProps<Theme>
  getHeadCellSx?: (headCell: HeadCell<K>) => SxProps<Theme> | undefined
  tableContainerProps?: Partial<TableContainerProps>
}

function DeliciousTable<T, K = unknown>({
  data,
  headCells,
  renderRow,
  orderBy,
  order,
  handleRequestSort,
  loading = false,
  error,
  totalCount,
  page,
  rowsPerPage,
  title = '',
  size = 'medium',
  onPageChange,
  onRowsPerPageChange,
  featureAvailable = true,
  noDataType = 'noData',
  noDataMessage,
  BodyComponent,
  RightHeaderComponent,
  stickyHeader = false,
  paperSx,
  tableContainerSx,
  tableSx,
  getHeadCellSx,
  tableContainerProps,
}: DeliciousTableProps<T, K>) {
  const { t } = useT()
  const resolvedPaperSx = paperSx
    ? [{ width: '100%' }, paperSx]
    : { width: '100%' }
  const typedPaperSx = resolvedPaperSx as SxProps<Theme>

  const TableToolbar = () => (
    <Toolbar sx={{ pl: 2, pr: 1 }}>
      <div style={{ flexGrow: 1 }}>
        <Typography variant="h6" id="tableTitle">
          {title}
        </Typography>
      </div>
      {RightHeaderComponent}
    </Toolbar>
  )

  const totalColumns = headCells.length

  return (
    <Paper sx={typedPaperSx}>
      {title && <TableToolbar />}
      <TableContainer sx={tableContainerSx} {...tableContainerProps}>
        <Table
          aria-labelledby="tableTitle"
          size={size}
          sx={tableSx}
          stickyHeader={stickyHeader}
        >
          <TableHead>
            <TableRow>
              {headCells.map(({ renderContent, ...headCell }) => (
                <TableCell
                  key={String(headCell.id)}
                  align={headCell.alignment}
                  padding={headCell.padding}
                  sortDirection={
                    orderBy && order && orderBy === headCell.id ? order : false
                  }
                  sx={
                    (stickyHeader
                      ? [
                          {
                            backgroundColor: 'background.paper',
                            top: 0,
                            zIndex: 2,
                          },
                          getHeadCellSx?.(headCell) || {},
                        ]
                      : getHeadCellSx?.(headCell)) as SxProps<Theme>
                  }
                >
                  {renderContent ? (
                    renderContent()
                  ) : headCell.sortKey ? (
                    <TableSortLabel
                      active={orderBy === headCell.sortKey}
                      direction={
                        orderBy === headCell.sortKey && order ? order : 'asc'
                      }
                      onClick={() => {
                        if (headCell.sortKey) {
                          handleRequestSort?.(headCell.sortKey)
                        }
                      }}
                    >
                      {headCell.tooltip ? (
                        <Tooltip title={headCell.tooltip} placement="top" arrow>
                          <span>{headCell.label}</span>
                        </Tooltip>
                      ) : (
                        headCell.label
                      )}
                    </TableSortLabel>
                  ) : headCell.tooltip ? (
                    <Tooltip title={headCell.tooltip} arrow>
                      <span>{headCell.label}</span>
                    </Tooltip>
                  ) : (
                    headCell.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {BodyComponent ||
              (loading ? (
                <>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={totalColumns} sx={{ py: 1 }}>
                        <Skeleton />
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              ) : error ? (
                <TableRow>
                  <TableCell
                    colSpan={totalColumns}
                    sx={{ textAlign: 'center' }}
                  >
                    <Alert severity="error" sx={{ display: 'inline-flex' }}>
                      {error}
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableNoData
                  totalColumns={totalColumns}
                  noDataType={noDataType}
                  message={noDataMessage}
                />
              ) : !featureAvailable ? (
                <FeatureUnavailable totalColumns={totalColumns} />
              ) : (
                <>{data.map(renderRow)}</>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      {totalCount !== undefined &&
        !loading &&
        rowsPerPage !== undefined &&
        page !== undefined &&
        onPageChange &&
        onRowsPerPageChange && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => onPageChange(newPage)}
            onRowsPerPageChange={event =>
              onRowsPerPageChange(parseInt(event.target.value, 10))
            }
            labelRowsPerPage={t('deliciousTable.rowsPerPage')}
            labelDisplayedRows={({ from, to, count, page }) =>
              t('deliciousTable.displayedRows', { from, to, count, page })
            }
          />
        )}
    </Paper>
  )
}

export default DeliciousTable
